import * as React from "react";
import { connect } from "react-redux";
import { ProjectService } from "../../../../services/project-service";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { PROJECT_LIST_CHANGED_EVENT } from "../../../../shared/utility/project-list-events";
import { setProjectCounts } from "../../../../store/management-layout/actions";
import { ISetProjectCountsParams } from "../../../../store/management-layout/types";
import { AppState } from "../../../../store/root-reducer";
import KuikaMenuItem from "../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import "./projects-left-panel.scss";

export interface IProjectLeftPanelState {}

export interface IProjectLeftPanelProps {
  url?: string;
}

interface IProjectLeftPanelStateProps {
  myAppsCount?: number;
  favoritesCount?: number;
}

interface IProjectLeftPanelDispatchProps {
  setProjectCounts: (params: ISetProjectCountsParams) => void;
}

type Props = IProjectLeftPanelProps & IWithHistoryProp & IProjectLeftPanelStateProps & IProjectLeftPanelDispatchProps;

class ProjectLeftPanel extends React.PureComponent<Props, IProjectLeftPanelState> {
  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  async componentDidMount(): Promise<void> {
    // Refresh counts when a project is favorited/deleted/duplicated/created elsewhere on the page.
    window.addEventListener(PROJECT_LIST_CHANGED_EVENT, this.handleProjectListChanged);
    await this.loadCounts();
  }

  componentWillUnmount(): void {
    window.removeEventListener(PROJECT_LIST_CHANGED_EVENT, this.handleProjectListChanged);
  }

  handleProjectListChanged = (): void => {
    void this.loadCounts();
  };

  loadCounts = async (): Promise<void> => {
    try {
      const res = await ProjectService.GetProjectCounts();
      if (res?.data) {
        this.props.setProjectCounts({
          myProjectsCount: res.data.myProjectsCount,
          favoritesCount: res.data.favoritesCount
        });
      }
    } catch {
      // non-critical enhancement — leave counts unset so the badges do not render
    }
  };

  renderCount = (count?: number): React.ReactNode => {
    if (count === undefined) return undefined;
    return <KuikaSmallMediumText className="kuika-projects__left-panel__count">{count}</KuikaSmallMediumText>;
  };

  render() {
    return (
      <div className="kuika-projects__left-panel">
        <div>
          <KuikaSmallMediumText className="kuika-projects__left-panel__section-header">APPS</KuikaSmallMediumText>
          <KuikaMenuItem
            data-testid="recentTab"
            data-onboarding-id="projects_panel_recent"
            title="Recent"
            leftIcon={<KuikaSvg name="history" />}
            selected={this.props.url === "recent" || this.props.url === "projects"}
            onClick={() => this.props.navigator.gotoRecent()}
          />
          <KuikaMenuItem
            data-testid="myAppsTab"
            data-onboarding-id="projects_panel_my-apps"
            title="My Apps"
            leftIcon={<KuikaSvg name="apps" />}
            selected={this.props.url === "myprojects"}
            onClick={() => this.props.navigator.gotoMyProjects()}
            rightUnit={this.renderCount(this.props.myAppsCount)}
          />
          <KuikaMenuItem
            data-testid="favoritesTab"
            data-onboarding-id="projects_panel_favorites"
            title="Favorites"
            leftIcon={<KuikaSvg name="star" />}
            selected={this.props.url === "favorites"}
            onClick={() => this.props.navigator.gotoFavorites()}
            rightUnit={this.renderCount(this.props.favoritesCount)}
          />
          <KuikaMenuItem
            data-testid="allTab"
            data-onboarding-id="projects_panel_all"
            title="All"
            leftIcon={<KuikaSvg name="folder-share" />}
            selected={this.props.url === "allprojects"}
            onClick={() => this.props.navigator.gotoAllProjects()}
          />
          <KuikaMenuItem
            data-testid="templatesTab"
            data-onboarding-id="projects_panel_templates"
            title="Templates"
            leftIcon={<KuikaSvg name="template" />}
            selected={this.props.url === "templates"}
            onClick={() => this.props.navigator.gotoTemplates()}
            className="kuika-projects__left-panel__template"
          />
        </div>
        <div className="kuika-projects__left-panel__bottom">
          <KuikaMenuItem
            title="Archive"
            leftIcon={<KuikaSvg name="archive" />}
            selected={this.props.url === "archive"}
            onClick={() => this.props.navigator.gotoArchive()}
            data-testid="recentlyDeletedTab"
            data-onboarding-id="projects_panel_archive"
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: AppState): IProjectLeftPanelStateProps => ({
  myAppsCount: state.managementLayout.myProjectsCount,
  favoritesCount: state.managementLayout.favoritesCount
});

const mapDispatchToProps: IProjectLeftPanelDispatchProps = {
  setProjectCounts
};

const projectLeftPanel = connect(
  mapStateToProps,
  mapDispatchToProps
)(withHistory<IProjectLeftPanelProps>(ProjectLeftPanel));
export { projectLeftPanel as ProjectLeftPanel };
